import { useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useFeatureNavigation } from '../hooks/useFeatureNavigation'
import { useLanguage } from '../contexts/LanguageContext'

const FeatureNavButtons = () => {
  const { navigateNext, navigatePrev, hasNext, hasPrev } = useFeatureNavigation()
  const { t } = useLanguage()

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return
      }

      if (event.key === 'ArrowLeft' && hasPrev) {
        event.preventDefault()
        navigatePrev()
      } else if (event.key === 'ArrowRight' && hasNext) {
        event.preventDefault()
        navigateNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigateNext, navigatePrev, hasNext, hasPrev])

  return (
    <>
      {/* Previous Button */}
      <AnimatePresence>
        {hasPrev && (
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            onClick={navigatePrev}
            className="fixed left-4 top-1/2 -translate-y-1/2 z-40 bg-black/80 backdrop-blur-xl border border-white/10 rounded-full p-3 hover:bg-white/10 transition-all shadow-2xl group"
            aria-label={t('features.navigation.previous')}
          >
            <ChevronLeft size={24} className="text-white group-hover:scale-110 transition-transform" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Next Button */}
      <AnimatePresence>
        {hasNext && (
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            onClick={navigateNext}
            className="fixed right-4 top-1/2 -translate-y-1/2 z-40 bg-black/80 backdrop-blur-xl border border-white/10 rounded-full p-3 hover:bg-white/10 transition-all shadow-2xl group"
            aria-label={t('features.navigation.next')}
          >
            <ChevronRight size={24} className="text-white group-hover:scale-110 transition-transform" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  )
}

export default FeatureNavButtons
